import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Loader2, ArrowRight } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COUNTRIES = [
  "Argentina", "Bolivia", "Brasil", "Chile", "Colombia", "Costa Rica", "Ecuador",
  "El Salvador", "España", "Guatemala", "Honduras", "México", "Nicaragua",
  "Panamá", "Paraguay", "Perú", "República Dominicana", "Uruguay", "Venezuela",
  "--Otro--",
];

function validateField(name, value) {
  switch (name) {
    case "name":
      if (!value.trim()) return "El nombre es obligatorio.";
      if (value.trim().length < 2) return "Mínimo 2 caracteres.";
      return "";
    case "company":
      if (!value.trim()) return "El nombre de la empresa es obligatorio.";
      if (value.trim().length < 2) return "Mínimo 2 caracteres.";
      return "";
    case "phone": {
      if (!value.trim()) return "El teléfono es obligatorio.";
      if (!/^\+?[0-9]+$/.test(value.trim())) return "Solo se permiten números y el símbolo +.";
      const digits = value.replace("+", "").trim();
      if (digits.length < 9) return "Mínimo 9 dígitos.";
      return "";
    }
    case "email":
      if (!value.trim()) return ""; // opcional
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())) return "Formato de email inválido.";
      return "";
    default:
      return "";
  }
}

const inputCls = (hasError) =>
  `h-12 text-base ${hasError ? "border-red-400 focus:ring-red-300/40" : ""}`;

export default function ContactForm() {
  const navigate = useNavigate();
  const [openedAt] = useState(() => Date.now());
  const [form, setForm] = useState({
    name: "", company: "", email: "", phone: "",
    country: "Chile", honeypot: "",
  });
  const [touched, setTouched] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const set = (k) => (e) => {
    const val = e.target.value;
    setForm((f) => ({ ...f, [k]: val }));
    if (touched[k]) {
      setFieldErrors((fe) => ({ ...fe, [k]: validateField(k, val) }));
    }
  };

  const blur = (k) => () => {
    setTouched((t) => ({ ...t, [k]: true }));
    setFieldErrors((fe) => ({ ...fe, [k]: validateField(k, form[k]) }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");

    const checks = ["name", "phone", "email", "company"];
    const newErrors = {};
    checks.forEach((k) => { newErrors[k] = validateField(k, form[k]); });
    setFieldErrors(newErrors);
    setTouched({ name: true, phone: true, email: true, company: true });

    if (Object.values(newErrors).some(Boolean)) return;

    setSubmitting(true);
    try {
      const now = Date.now();
      const elapsed_ms = now - openedAt;
      const jsToken = btoa(`${now}|${Math.floor(now / 1000) % 4999}`);
      const payload = {
        name: form.name.trim(),
        company: form.company.trim(),
        email: form.email.trim() || null,
        phone: form.phone.trim(),
        country: form.country || null,
        elapsed_ms,
        js_token: jsToken,
        honeypot: form.honeypot,
      };
      await axios.post(`${API}/contact`, payload);
      navigate("/contacto-gracias");
    } catch (err) {
      const msg = err?.response?.data?.detail || "No se pudo enviar el formulario. Inténtalo de nuevo.";
      setError(msg);
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="contact-form" noValidate>
      {/* honeypot */}
      <input
        type="text"
        name="website"
        value={form.honeypot}
        onChange={set("honeypot")}
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }}
      />

      <div className="grid sm:grid-cols-2 gap-5">
        <Field label="Tu nombre" required error={fieldErrors.name}>
          <Input
            data-testid="contact-name"
            value={form.name}
            onChange={set("name")}
            onBlur={blur("name")}
            placeholder="Tu nombre completo"
            maxLength={50}
            className={inputCls(fieldErrors.name)}
          />
        </Field>
        <Field label="Teléfono / WhatsApp" required error={fieldErrors.phone}>
          <Input
            data-testid="contact-phone-input"
            value={form.phone}
            onChange={set("phone")}
            onBlur={blur("phone")}
            placeholder="+56 9 ..."
            maxLength={12}
            className={inputCls(fieldErrors.phone)}
          />
        </Field>
        <Field label="Email" error={fieldErrors.email}>
          <Input
            data-testid="contact-email-input"
            type="email"
            value={form.email}
            onChange={set("email")}
            onBlur={blur("email")}
            placeholder="nombre@empresa.com"
            maxLength={50}
            className={inputCls(fieldErrors.email)}
          />
        </Field>
        <Field label="País">
          <select
            data-testid="contact-country"
            value={form.country}
            onChange={set("country")}
            className="flex h-12 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-base shadow-sm focus:outline-none focus:ring-2 focus:ring-navy-800/30 focus:border-navy-800"
          >
            {COUNTRIES.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </Field>
        <Field label="Nombre de la empresa" required error={fieldErrors.company} wrapperClassName="sm:col-span-2">
          <Input
            data-testid="contact-company"
            value={form.company}
            onChange={set("company")}
            onBlur={blur("company")}
            placeholder="Nombre de tu empresa"
            maxLength={50}
            className={inputCls(fieldErrors.company)}
          />
        </Field>
      </div>

      {error && (
        <p data-testid="contact-error" className="text-sm text-red-600 font-medium">
          {error}
        </p>
      )}

      <button
        type="submit"
        disabled={submitting}
        data-testid="contact-submit"
        className="btn-primary w-full text-base disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {submitting ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            Enviando…
          </>
        ) : (
          <>
            Solicitar llamada introductoria
            <ArrowRight className="w-4 h-4" />
          </>
        )}
      </button>

      <p className="text-xs text-slate-500 mt-2">
        ¿Prefieres escribir directamente?{" "}
        <a
          href={`https://wa.me/56999176009?text=${encodeURIComponent("Hola Carlos, me gustaría conversar sobre EOS para mi empresa.")}`}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="contact-whatsapp-link"
          className="font-bold text-[#25D366] hover:underline"
        >
          WhatsApp →
        </a>
      </p>
    </form>
  );
}

function Field({ label, required, error, wrapperClassName = "", children }) {
  return (
    <div className={`space-y-2 ${wrapperClassName}`}>
      <Label className="text-[13px] font-bold tracking-wider uppercase text-navy-800">
        {label} {required && <span className="text-[#F26522]">*</span>}
      </Label>
      {children}
      {error && (
        <p className="text-xs text-red-500 font-medium">{error}</p>
      )}
    </div>
  );
}
